import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { authFetch } from '../../api';

const { FiShield, FiLock, FiCheckCircle, FiXCircle, FiLoader, FiArrowRight } = FiIcons;

const Setup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [error, setError] = useState(null);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Missing setup token.');
      setValidating(false);
      setLoading(false);
      return;
    }

    const validateToken = async () => {
      const res = await authFetch(`setup/validate?token=${token}`);
      if (res.status === 'success') {
        setAdmin(res.admin);
      } else {
        setError(res.error || 'Invalid or expired setup token.');
      }
      setValidating(false);
      setLoading(false);
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      alert('Passwords do not match.');
      return;
    }
    if (password.length < 12) {
      alert('Password must be at least 12 characters.');
      return;
    }

    setSubmitting(true);
    const res = await authFetch('setup/complete', {
      method: 'POST',
      body: JSON.stringify({ token, password })
    });

    if (res.status === 'success') {
      setSuccess(true);
      setTimeout(() => navigate('/admin/login'), 3000);
    } else {
      alert(res.error || 'Failed to complete setup.');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <SafeIcon icon={FiLoader} className="text-4xl text-indigo-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center border border-red-100">
          <SafeIcon icon={FiXCircle} className="text-5xl text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Setup Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => navigate('/admin/login')}
            className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold hover:bg-gray-800 transition-colors"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center border border-green-100">
          <SafeIcon icon={FiCheckCircle} className="text-5xl text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Setup Complete!</h1>
          <p className="text-gray-600 mb-6">Your account has been activated. Redirecting to login...</p>
          <div className="w-full bg-gray-100 h-1 rounded-full overflow-hidden">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration: 3 }}
              className="bg-green-500 h-full"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full border border-indigo-100"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-indigo-100 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiShield} className="text-3xl" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Complete Account Setup</h1>
          <p className="text-gray-600 mt-2">Welcome, {admin?.display_name || 'Administrator'}</p>
          <p className="text-sm text-gray-500">{admin?.email}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <div className="relative">
              <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                required
                minLength={12}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="At least 12 characters"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <div className="relative">
              <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                required
                minLength={12}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
              />
            </div>
          </div>

          <div className="text-xs text-gray-500 bg-gray-50 p-4 rounded-xl border border-gray-100">
            <ul className="list-disc pl-4 space-y-1">
              <li>Minimum 12 characters required.</li>
              <li>Use a combination of letters, numbers, and symbols.</li>
              <li>Avoid using personal information.</li>
            </ul>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {submitting ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiArrowRight} />}
            Activate Account
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default Setup;